// Built-in ecommerce API endpoint registry. Single source of truth for
// both the designer dropdowns (which built-in endpoint labels to show)
// and the runtime fetcher (which URL to call when the source is set to
// a built-in id). Adding a new built-in endpoint here automatically
// surfaces it in the EcomSourceEditor dropdown.

import type { EcomEndpointId, EcomSourceApi } from '@/pages/designers/page/types'

export interface EcomEndpointArgTarget {
  name: string                              // API param key
  label?: string
  hint?: string
}

export interface EcomEndpointOutputTarget {
  name: string                              // key on the emitted record
  label?: string
  hint?: string
}

export interface EcomEndpointDef {
  id: EcomEndpointId
  label: string                             // shown in dropdown
  method: 'GET' | 'POST'
  url: string                               // base path, no query
  description?: string
  argTargets: EcomEndpointArgTarget[]       // populates ArgMappingEditor for source.args
  outputTargets: EcomEndpointOutputTarget[] // populates ArgMappingEditor for outputBindings
}

type BuiltInId = Exclude<EcomEndpointId, 'custom'>

export const ECOM_ENDPOINTS: Record<BuiltInId, EcomEndpointDef> = {
  customers: {
    id: 'customers',
    label: 'Customers (default)',
    method: 'GET',
    url: '/proteus-vision-ecom-ext/customers',
    description: 'Vision ERP customer search. Returns { items: ErpCustomer[] }.',
    argTargets: [
      { name: 'q', label: 'Search query' },
      { name: 'limit', label: 'Limit', hint: 'default 20' },
    ],
    outputTargets: [
      { name: 'value', label: 'Selected record (whole)' },
      { name: 'id', label: 'Customer id' },
      { name: 'name', label: 'Customer name' },
      { name: 'phone', label: 'Phone' },
      { name: 'email', label: 'Email' },
    ],
  },
  products: {
    id: 'products',
    label: 'Products (default)',
    method: 'GET',
    url: '/proteus-vision-ecom-ext/products',
    description: 'Vision ERP product catalogue.',
    argTargets: [
      { name: 'q', label: 'Search query' },
      { name: 'category', label: 'Category' },
      { name: 'page', label: 'Page', hint: '1-based' },
      { name: 'pageSize', label: 'Page size' },
      { name: 'sort', label: 'Sort', hint: 'price_asc | price_desc | popular | newest' },
    ],
    outputTargets: [
      { name: 'cart', label: 'Cart array', hint: 'list of configured line items' },
    ],
  },
  sales_orders: {
    id: 'sales_orders',
    label: 'Sales orders (default)',
    method: 'POST',
    url: '/proteus-vision-ecom-ext/sales-orders',
    description: 'Vision ERP sales order create.',
    argTargets: [
      { name: 'customerId', label: 'Customer id' },
      { name: 'lines', label: 'Order lines' },
      { name: 'notes', label: 'Order notes' },
    ],
    outputTargets: [
      { name: 'value', label: 'Created record (whole)' },
      { name: 'orderId', label: 'Order id' },
      { name: 'orderNumber', label: 'Order number' },
      { name: 'status', label: 'Status' },
      { name: 'totalAmount', label: 'Total amount' },
      { name: 'currency', label: 'Currency' },
    ],
  },
}

/** Ordered list for dropdown rendering. 'custom' is appended at the end. */
export const ECOM_ENDPOINT_OPTIONS: { id: EcomEndpointId; label: string }[] = [
  ...Object.values(ECOM_ENDPOINTS).map((e) => ({ id: e.id, label: e.label })),
  { id: 'custom', label: 'Custom URL…' },
]

/** Resolve a configured source to its effective URL. Returns null when
 *  the source is `custom` but no customUrl was provided. */
export function resolveEndpointUrl(source: EcomSourceApi): string | null {
  if (source.endpointId === 'custom') {
    const u = source.customUrl?.trim()
    return u || null
  }
  const def = ECOM_ENDPOINTS[source.endpointId as BuiltInId]
  return def ? def.url : null
}

/** Resolve the HTTP method for a source. Custom URLs default to GET
 *  unless the block opts into POST elsewhere (only the NewCustomerForm
 *  / OrderSubmit blocks POST today; they pass method explicitly via
 *  the apiPost helper). */
export function resolveEndpointMethod(source: EcomSourceApi): 'GET' | 'POST' {
  if (source.endpointId === 'custom') return 'GET'
  const def = ECOM_ENDPOINTS[source.endpointId as BuiltInId]
  return def?.method || 'GET'
}

/** Lookup a built-in definition by id. Returns null for 'custom'. */
export function getEndpointDef(id: EcomEndpointId): EcomEndpointDef | null {
  if (id === 'custom') return null
  return ECOM_ENDPOINTS[id as BuiltInId] || null
}
